// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth/auth-provider";
import { needsSecondFactor } from "@/lib/auth/second-factor";

/**
 * Shown once per interactive login (not on page reloads) to editors without MFA or a passkey, warning that
 * one will soon be required. Either button acknowledges it until the next login.
 */
export function SecondFactorNoticeDialog() {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { user, isFreshLogin, acknowledgeFreshLogin } = useAuth();

  const open = isFreshLogin && needsSecondFactor(user);

  function onSetUp() {
    acknowledgeFreshLogin();
    navigate({ to: "/profile", search: { t: "security" } });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) acknowledgeFreshLogin();
      }}
    >
      <DialogContent
        id="dialog-second-factor-notice"
        className="sm:max-w-[30rem] p-0 gap-0 overflow-hidden flex flex-col"
      >
        <div className="flex flex-col items-center gap-4 p-6 pb-4 text-center">
          <img src="/two-factor-light.webp" alt="" className="accent-tint h-44 w-auto dark:hidden" />
          <img src="/two-factor-dark.webp" alt="" className="accent-tint hidden h-44 w-auto dark:block" />
          <DialogHeader className="sm:text-center">
            <DialogTitle>{t("secondFactorNotice.title")}</DialogTitle>
            <DialogDescription>{t("secondFactorNotice.description")}</DialogDescription>
          </DialogHeader>
        </div>
        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={acknowledgeFreshLogin}>
            {t("secondFactorNotice.later")}
          </Button>
          <Button onClick={onSetUp}>
            <ShieldAlert className="h-4 w-4" />
            {t("secondFactorNotice.setUp")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
